import React, { useEffect, useRef, useState, useCallback } from 'react';
import { TableData } from 'models/ai-chat/visualization.types';
import * as XLSX from 'xlsx';
import { useChat } from 'store/useChatStore';
import { TableRequest } from 'models/ai-chat/table-pagination';
import { useAppSelector } from 'redux/hooks';
import { RootState } from 'redux/store';
import AppliedFiltersDisplay from './AppliedFiltersDisplay';
import { FilterItem } from 'models/ai-chat/filter-request';

interface TableFullViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: TableData;
  sortConfig: { column: number; direction: 'asc' | 'desc' } | null;
  onSort: (columnIndex: number) => void;
  filteredAndSortedRows: any[][];
  isNumericColumn: (columnIndex: number) => boolean;
  formatCellValue: (cell: any, columnIndex?: number) => any;
  onExport: () => void;
  request: TableRequest | null;
  onDataUpdate: (newData: TableData) => void;
  // New props for AppliedFiltersDisplay
  sqlQuery?: string;
  messageId?: string;
  onFilterValueChange?: (filter: any, newValue: string) => void;
  onFilterUpdateComplete?: (updatedData: any) => void;
  filterData?: FilterItem[];
  customerId?: string;
  onExportExcel?: () => void;
  onDownloadPDF?: () => void;
}

const TableFullViewModal: React.FC<TableFullViewModalProps> = ({
  isOpen,
  onClose,
  data,
  onSort,
  filteredAndSortedRows,
  isNumericColumn,
  formatCellValue,
  onExport,
  request,
  onDataUpdate,
  // New props for AppliedFiltersDisplay
  sqlQuery,
  messageId,
  onFilterValueChange,
  onFilterUpdateComplete,
  filterData,
  customerId,
  onExportExcel,
  onDownloadPDF
}) => {

  const { tableRows, loading } = useAppSelector((state: RootState) => state.chat);
  const { getTableDataRequest } = useChat();
  const [sortConfig, setSortConfig] = useState<{ column: number; direction: 'asc' | 'desc' } | null>(null);

  // Helper functions for image detection and rendering
  const isImageColumn = (columnIndex: number) => {
    if (!data?.headers?.[columnIndex]) return false;
    const header = data.headers[columnIndex].toString().toLowerCase();
    const imageKeywords = ['product_image', 'image', 'photo', 'picture', 'img', 'thumbnail'];
    return imageKeywords.some(keyword => header.includes(keyword));
  };

  const isImageUrl = (value: any) => {
    if (!value || typeof value !== 'string') return false;
    const url = value.toLowerCase().trim();
    return (
      url.startsWith('http://') || 
      url.startsWith('https://') || 
      url.startsWith('//')
    ) && (
      url.includes('.jpg') || 
      url.includes('.jpeg') || 
      url.includes('.png') || 
      url.includes('.gif') || 
      url.includes('.webp') || 
      url.includes('.svg')
    );
  };

  const renderCellContent = (cell: any, columnIndex: number) => {
    // Check if this is an image column and the value is an image URL
    if (isImageColumn(columnIndex) && isImageUrl(cell)) {
      return (
        <div className="flex items-center justify-center">
          <img 
            src={String(cell).trim()} 
            alt="Product" 
            className="w-12 h-12 object-cover rounded border"
            onError={(e) => {
              // Fallback to text if image fails to load
              const img = e.currentTarget as HTMLImageElement;
              const nextSibling = img.nextElementSibling as HTMLElement;
              if (img && nextSibling) {
                img.style.display = 'none';
                nextSibling.style.display = 'block';
              }
            }}
          />
          <span style={{ display: 'none' }} className="text-xs text-gray-500 truncate">
            {formatCellValue(cell, columnIndex)}
          </span>
        </div>
      );
    }
    
    // Default text rendering
    return formatCellValue(cell, columnIndex);
  };
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [allRows, setAllRows] = useState<any[][]>([]);

//   useEffect(()=>{
//     setIsLoadingMore(loading)
//   },[loading])

  useEffect(() => {
    if (data?.rows) {
      setAllRows(data.rows);
      setCurrentPage(request?.page || 1);
      setHasMoreData(true);
      // console.log('Initialized with:', {
      //   rowCount: data.rows.length,
      //   currentPage: request?.page || 1,
      //   hasMoreData: true
      // });
    }
  }, [data, request?.page]);

    const handleSort = (columnIndex: number) => {
    setSortConfig(prev => {
      if (prev?.column === columnIndex) {
        return { column: columnIndex, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { column: columnIndex, direction: 'asc' };
    });
  };

    if (sortConfig) {
    const { column, direction } = sortConfig;
    filteredAndSortedRows.sort((a, b) => {
      const valA = a[column];
      const valB = b[column];
      
      // Check for empty values
      const isEmptyA = valA === null || valA === undefined || valA === '' || 
                       String(valA).toLowerCase() === 'null';
      const isEmptyB = valB === null || valB === undefined || valB === '' || 
                       String(valB).toLowerCase() === 'null';
      
      // For numeric columns: empty values always go to bottom
      if (isNumericColumn(column)) {
        if (isEmptyA && isEmptyB) return 0;
        if (isEmptyA) return 1;  // A goes to bottom
        if (isEmptyB) return -1; // B goes to bottom
        
        // Parse as numbers for proper numeric sorting
        const numA = parseFloat(String(valA).replace(/[,$%\s]/g, ''));
        const numB = parseFloat(String(valB).replace(/[,$%\s]/g, ''));
        
        if (!isNaN(numA) && !isNaN(numB)) {
          return direction === 'asc' ? numA - numB : numB - numA;
        }
      }
      
      // For non-numeric columns: use string comparison
      const formattedA = String(formatCellValue(valA, column)).toLowerCase();
      const formattedB = String(formatCellValue(valB, column)).toLowerCase();
      
      if (formattedA < formattedB) return direction === 'asc' ? -1 : 1;
      if (formattedA > formattedB) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  }


  useEffect(()=>{
    if(tableRows && tableRows.data.rows){
        const updatedRows = [...allRows, ...tableRows.data.rows];
        setAllRows(updatedRows);
        setCurrentPage(tableRows.page);
        setHasMoreData(tableRows.has_more);

        // const updatedData: TableData = {
        //   ...data,
        //   rows: updatedRows
        // };
        // onDataUpdate(updatedData);
    }
  }, [tableRows])

  const loadMoreData = useCallback(async () => {
    if (!request || isLoadingMore || !hasMoreData || loading) return;

    // Skip API call if we have less than 10 records (no need for pagination)
    if (allRows.length < 10) {
      console.log('Skipping API call - less than 10 records:', allRows.length);
      setHasMoreData(false);
      return;
    }

   // console.log('Loading more data - Page:', currentPage + 1);
    setIsLoadingMore(true);
    
    try {
      const nextPage = currentPage + 1;
      const newRequest: TableRequest = {
        ...request,
        page: nextPage,
        page_size: request.page_size || 50
      };

      console.log('Making API call with request:', newRequest);
      const response = await getTableDataRequest(newRequest);
      console.log('API response:', response);

    } catch (error) {
      console.error('Error loading more data:', error);
      setHasMoreData(false);
    } finally {
      setIsLoadingMore(false);
    }
  }, [request, currentPage, isLoadingMore, hasMoreData, allRows.length, getTableDataRequest]);

  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current || isLoadingMore || !hasMoreData) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
    const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;
    
    // console.log('Scroll detected:', {
    //   scrollTop,
    //   scrollHeight,
    //   clientHeight,
    //   scrollPercentage,
    //   isLoadingMore,
    //   hasMoreData,
    //   globalLoading: loading
    // });

    if (scrollPercentage >= 0.85) {
     // console.log('Triggering load more data');
      loadMoreData();
    }
  }, [loadMoreData, isLoadingMore, hasMoreData]);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    let timeoutId: ReturnType<typeof setTimeout>;
    const throttledHandleScroll = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(handleScroll, 100); 
    };

   // console.log('Adding scroll listener');
    scrollContainer.addEventListener('scroll', throttledHandleScroll);
    
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      scrollContainer.removeEventListener('scroll', throttledHandleScroll);
      console.log('Removing scroll listener');
    };
  }, [handleScroll]);

  useEffect(() => {
    if (isOpen && data?.rows) {
      setAllRows(data.rows);
      setCurrentPage(request?.page || 1);
      setHasMoreData(true);
      setIsLoadingMore(false);
    }
  }, [isOpen, data, request?.page]);

  useEffect(() => {
    if (isOpen && allRows.length > 0 && hasMoreData && !isLoadingMore) {
      // Skip if we have less than 10 records
      if (allRows.length < 10) {
        console.log('Skipping initial load - less than 10 records:', allRows.length);
        setHasMoreData(false);
        return;
      }

      setTimeout(() => {
        if (scrollContainerRef.current) {
          const { scrollHeight, clientHeight } = scrollContainerRef.current;
          console.log('Initial content check:', { scrollHeight, clientHeight, loading });
          if (scrollHeight <= clientHeight) {
            console.log('Content too small, loading more data immediately');
            loadMoreData();
          }
        }
      }, 100);
    }
  }, [isOpen, allRows.length, hasMoreData, isLoadingMore, loadMoreData]);

  if (!isOpen) return null;

//   console.log('TableFullViewModal render:', {
//     isOpen,
//     hasRequest: !!request,
//     currentPage,
//     isLoadingMore,
//     globalLoading: loading,
//     hasMoreData,
//     allRowsLength: allRows.length,
//     filteredRowsLength: filteredAndSortedRows.length
//   });

  return (
    <div className="fixed inset-0 bg-white z-50 flex flex-col">
      {/* Modal Header with Applied Filters */}
      <div className="p-4 border-b border-gray-200 bg-white shadow-sm">
        {/* Applied Filters Display */}
        {sqlQuery && (
          <AppliedFiltersDisplay
            sqlQuery={sqlQuery}
            onFilterValueChange={onFilterValueChange}
            onFilterUpdateComplete={onFilterUpdateComplete}
            customerId={customerId}
            filterData={filterData}
            messageId={messageId}
            hasChartSwitcher={false}
            pdfGeneration={false}
            onExportExcel={onExportExcel}
            onDownloadPDF={onDownloadPDF}
            chartData={null}
            chartType="table"
            chartMetadata={null}
            onFullView={onClose}
            isInModal={true}
          />
        )}
      </div>
      
      {/* Modal Content */}
      <div 
        ref={scrollContainerRef}
        className="flex-1 overflow-auto"
        style={{ scrollBehavior: 'smooth' }}
      >
        <div className="min-w-full">
          <table className="w-full border-collapse text-LimedSpruce">
            <thead className="bg-white sticky top-0 z-10">
              <tr>
                {data.headers.map((header, index) => (
                  <th
                    key={index}
                    onClick={() => handleSort(index)}
                    className={`px-4 py-3 text-ai80 text-gray80 font-semibold tracking-wider text-Cello border-b border-gray-200 cursor-pointer relative capitalize whitespace-nowrap ${
                      isImageColumn(index) ? 'text-center' : 
                      isNumericColumn(index) ? 'text-right' : 'text-left'
                    }`}
                    style={{ fontWeight: 600, textTransform: 'capitalize' }}
                  >
                    <div className={`flex items-center space-x-1 ${
                      isImageColumn(index) ? 'justify-center' :
                      isNumericColumn(index) ? 'justify-end' : 'justify-start'
                    }`}>
                      <span>{header}</span>
                      <span>
                        {sortConfig?.column === index
                          ? (sortConfig.direction === 'asc' ? '↑' : '↓')
                          : ''}
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {allRows.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  className={`${
                    rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                  } hover:bg-blue-50 transition-all duration-200`}
                >
                  {row.map((cell, cellIndex) => (
                    <td
                      key={cellIndex}
                      className={`px-4 py-3 text-gray-800 border-b border-gray-100 whitespace-nowrap ${
                        isImageColumn(cellIndex) ? 'text-center' :
                        isNumericColumn(cellIndex) ? 'text-right' : 'text-left'
                      }`}
                    >
                      <div 
                        className={`${
                          isImageColumn(cellIndex) ? '' : 'overflow-hidden text-ellipsis'
                        }`}
                        title={String(formatCellValue(cell, cellIndex))}
                      >
                        {renderCellContent(cell, cellIndex)}
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          
         {(isLoadingMore || loading) && (
            <div className="flex justify-center items-center py-8 bg-white">
              <div className="flex items-center space-x-3">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <span className="text-sm text-gray-600">
                  {loading ? 'Loading data...' : `Loading page ${currentPage + 1}...`}
                </span>
              </div>
            </div>
          )}
          
          {!hasMoreData && allRows.length > 0 && !isLoadingMore && (
            <div className="flex justify-center items-center py-6 bg-gray-50">
              <div className="flex items-center space-x-2">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.25} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm text-gray-500">All data loaded</span>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Modal Footer */}
      <div className="flex flex-col sm:flex-row justify-between items-center p-4 border-t border-gray-200 bg-white shadow-sm">
        <div className="flex items-center space-x-4">
          <span className="custom-border flex items-center px-4 py-1 text-xs text-primary hover:bg-[#e6e6e6] rounded transition-colors">
            Showing {allRows.length} of {allRows.length} rows
            {hasMoreData && !isLoadingMore && <span className="ml-1">(more available)</span>}
          </span>
          {isLoadingMore && (
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary"></div>
              <span className="text-xs text-gray-500">
                Loading page {currentPage + 1}...
              </span>
            </div>
          )}
        </div>
        <div className="flex space-x-2">
          <button
            onClick={onExport}
            className="custom-border flex items-center px-4 py-1 text-xs text-primary hover:bg-[#e6e6e6] rounded transition-colors"
            title="Export to Excel"
          >
            Export
          </button>
          <button
            onClick={onClose}
            className="px-4 py-1 bg-primary text-white text-xs rounded hover:bg-[#0041a6] transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TableFullViewModal;